import applyCouponQuery from '@tcp/core/src/queries/applyCoupon.query.graphql';
import applyMultipleCouponQuery from '@tcp/core/src/queries/applyMultipleCoupon.query.graphql';
import applyBonusPointDay from '@tcp/core/src/queries/applyBonusPointDay.query.graphql';
import removeCouponOrPromoQuery from '@tcp/core/src/queries/deleteCoupon.query.graphql';
import removeBonusPointDay from '@tcp/core/src/queries/removeBonusPointDay.query.graphql';
import getPromotionDetailsQuery from '@tcp/core/src/queries/getPromotionDetails.query.graphql';
import { checkCache, getDateObject } from '@tcp/core/src/utils';
import logger from '@tcp/core/src/utils/loggerInstance';
import isEmpty from 'lodash/isEmpty';
import { SubmissionError } from 'redux-form'; // ES6
import CheckoutConstants from '@tcp/core/src/components/features/CnC/Checkout/Checkout.constants';
import getCartCachedQuery from '@tcp/core/src/queries/getCartCached.query.graphql';
import getBonusPoints from '@tcp/core/src/queries/getBonusPoints.query.graphql';
import { executeStatefulGraphQLQuery } from '../../handler/handler';
import {
  getFormattedError,
  getDynamicCodeErrorMessage,
  getFreeShippingMPRErrorMessage,
} from '../../../utils/errorMessage.util';
import { constructCouponStructure, COUPON_STATUS } from '../CnC/CartItemTile';

export const setPlaceCashError = ({ err: errorObj }) => {
  const err = errorObj;
  let params = err[0] && err[0].errorParameters;
  params = params && Object.values(params);
  const isPromoNotAvailable =
    err[0].errorCode === CheckoutConstants.ERR_PROMOTION_NOT_AVAILABLE_AT_THIS_TIME;
  const isPlaceCashError =
    params &&
    (params[0] === CheckoutConstants.PLACE_CASH || (isPromoNotAvailable && params[0] === 'PLCC'));

  if (isPlaceCashError && !isPromoNotAvailable) {
    err[0].errorCode = CheckoutConstants.PLACE_CASH_ERROR;
  }
  return isPlaceCashError;
};

export const updateQueryForBonusPoints = (cache, data) => {
  let couponResp = {};
  if (data && (data.removeBonusDayFromBasket || data.applyBonusDayToBasket)) {
    couponResp = data.removeBonusDayFromBasket || data.applyBonusDayToBasket;
  }
  const { c_isBPDAppliedToBasket: isBPDAppliedToBasket = false } = couponResp;
  const isBonudDayCacheExist = checkCache(cache, 'getBonusDay');
  if (isBonudDayCacheExist) {
    const storedData = cache.readQuery({ query: getBonusPoints });
    const updateData = { ...storedData.getBonusDay, isBPDAppliedToBasket };
    cache.writeQuery({
      query: getBonusPoints,
      data: { getBonusDay: updateData },
    });
  }
};

export const errorHandler = (err, errorsMapping, couponCode) => {
  let errCouponCode = couponCode;
  const isPlaceCashError = setPlaceCashError({ err });
  const couponError = [...err];
  const couponErr = couponError.length && couponError[0];
  let missingAmount = 0;
  let freeShippingMPRError = false;
  if (couponErr && couponErr.errorParameters) {
    missingAmount = couponErr.errorParameters.amountMissing;
    const params = couponErr.errorParameters;
    const paramArray = params && Object.keys(params);
    couponErr.errorParameters = paramArray.toString();
    if (params && Object.values(params)[0] === 'FREESHIPPING_MPR') {
      freeShippingMPRError = true;
    }
  }
  const formattedErrorReq = { errorResponse: couponErr };
  const error = getFormattedError(formattedErrorReq, errorsMapping, true);
  if (
    couponErr &&
    (couponErr.errorCode === CheckoutConstants.TCP_PROMOTION_LOYALTY_THRESHOLD_NOT_MET ||
      couponErr.errorCode === CheckoutConstants.TCP_PROMOTION_PLACECASH_THRESHOLD_NOT_MET)
  ) {
    errCouponCode = missingAmount;
  }
  getDynamicCodeErrorMessage(error, errCouponCode);
  if (freeShippingMPRError) {
    getFreeShippingMPRErrorMessage(error, couponErr);
  }
  error.errorMessages = error.errorMessages || {
    _error: CheckoutConstants.SOMETHING_WENT_WRONG,
  };
  const { errorMessages: { _error = '' } = {} } = error;
  let { errorMessages } = error;

  errorMessages = {
    ...errorMessages,
    errorCode: couponErr?.errorCode || '',
    _error: { msg: _error, isPlaceCashError },
  };

  throw new SubmissionError(errorMessages);
};
// eslint-disable-next-line complexity
export const updateQuery = (cache, data) => {
  let couponResp = {};

  if ((data && data.applyCouponToBasket) || data?.applyMultipleCoupons) {
    couponResp = data.applyCouponToBasket || data.applyMultipleCoupons;
  }
  const { c_couponMap: couponMap, ...dataToUpdate } = couponResp;
  let isCacheExist = false;
  if (!isEmpty(dataToUpdate)) {
    isCacheExist = checkCache(cache);
  }
  if (isCacheExist && dataToUpdate) {
    cache.writeQuery({
      query: getCartCachedQuery,
      data: { getCart: dataToUpdate },
      // broadcast: false,
    });
  }
};

export const applyMultipleCouponsToBasket = (couponCode, errorsMapping) => {
  const query = applyMultipleCouponQuery;
  const variables = {
    multipleCouponsRequest: { coupons: [{ codes: couponCode, couponType: 'PLACECASH' }] },
    fromCartPage: false,
  };

  return executeStatefulGraphQLQuery({
    query,
    variables,
    type: 'mutation',
    updateQuery,
  })
    .then((res) => {
      if (res.errors) {
        const err = res.errors;
        errorHandler(err, errorsMapping);
      }
      return res.data && res.data.applyMultipleCoupons;
    })
    .catch((err) => {
      errorHandler(err, errorsMapping);
    });
};

export const applyCouponToBasket = (
  { couponCode = '' },
  errorsMapping,
  cartItemsCount,
  fromCartPage,
  fromBonusPointsDay,
  isCouponOnHoldEmptyCartEnabled
) => {
  if (!cartItemsCount && !isCouponOnHoldEmptyCartEnabled) {
    const errorMessages = {
      _error: {
        msg: errorsMapping.TCP_PROMOTION_COUPON_ON_EMPTY_CART,
      },
    };
    throw new SubmissionError(errorMessages);
  }

  couponCode.trim().toUpperCase();

  let query;
  let variables;
  if (fromBonusPointsDay) {
    query = applyBonusPointDay;
    variables = {};
  } else {
    query = applyCouponQuery;
    variables = {
      couponDto: {
        code: couponCode,
      },
    };
  }

  return executeStatefulGraphQLQuery({
    query,
    variables,
    type: 'mutation',
    header: { fromCartPage },
    updateQuery: fromBonusPointsDay ? updateQueryForBonusPoints : updateQuery,
  })
    .then((res) => {
      if (res.errors) {
        const err = res.errors;
        errorHandler(err, errorsMapping, couponCode);
      }
      return !fromBonusPointsDay
        ? res.data && res.data.applyCouponToBasket
        : res.data && res.data.applyBonusDayToBasket;
    })
    .catch((err) => {
      errorHandler(err, errorsMapping, couponCode);
    });
};

// eslint-disable-next-line complexity
export const updateQueryForRemoveCoupon = (cache, data) => {
  let couponResp = {};
  if (data && data.deleteCouponFromBasket) {
    couponResp = data.deleteCouponFromBasket;
  }
  const { c_couponMap: couponMap, ...dataToUpdate } = couponResp;
  let isCacheExist = false;
  if (dataToUpdate) {
    isCacheExist = checkCache(cache);
  }
  if (isCacheExist && dataToUpdate) {
    cache.writeQuery({
      query: getCartCachedQuery,
      data: { getCart: dataToUpdate },
      // broadcast: false,
    });
  }
};
export const removeCouponOrPromo = (
  { couponCode = '' },
  errorsMapping,
  fromCartPage,
  fromBonusPointsDay
) => {
  let query;
  let variables;
  if (fromBonusPointsDay) {
    query = removeBonusPointDay;
    variables = {};
  } else {
    query = removeCouponOrPromoQuery;
    variables = {
      couponId: couponCode,
    };
  }
  return executeStatefulGraphQLQuery({
    query,
    variables,
    type: 'mutation',
    header: { fromCartPage },
    updateQuery: fromBonusPointsDay ? updateQueryForBonusPoints : updateQueryForRemoveCoupon,
  })
    .then((res) => {
      if (res.errors) {
        const err = res.errors;
        errorHandler(err, errorsMapping);
      }
      return !fromBonusPointsDay
        ? res.data && res.data.deleteCouponFromBasket
        : res.data && res.data.removeBonusDayFromBasket;
    })
    .catch((err) => {
      errorHandler(err, errorsMapping);
    });
};
const getCouponDetails = (codeTypeMap, code) => {
  return codeTypeMap.find((rcd) => (rcd?.code || rcd?.couponCode) === code);
};

export const isValidEndDate = (dateString) => {
  const date = new Date(dateString);
  // eslint-disable-next-line no-restricted-globals
  return date instanceof Date && !isNaN(date);
};

export const convertExpiryDateToPST = (endDate, couponExpiryTimeDiff) => {
  const utcDate = isValidEndDate(endDate) ? new Date(endDate) : endDate;
  if (isValidEndDate(utcDate)) {
    utcDate.setMinutes(utcDate.getMinutes() - couponExpiryTimeDiff);
  }
  const updatedDateISO = isValidEndDate(utcDate) ? utcDate.toISOString() : endDate;
  // endDate is in UTC
  const newEndData = updatedDateISO;
  let endDatepst = newEndData;
  if (newEndData) {
    const timezone = 'America/Los_Angeles';
    endDatepst = getDateObject(
      getDateObject(newEndData)?.toLocaleString('en-US', {
        timeZone: timezone,
      })
    );
  }
  return endDatepst;
};

const simulateToOffer = (
  couponList,
  codeTypeMap = [],
  isCouponOnHoldNewDesignEnabled,
  couponExpiryTimeDiff = 1,
  couponDescriptonFromPriceAdjustments = {}
) => {
  const now = getDateObject();
  const oneDay = 24 * 60 * 60 * 1000;
  return couponList.map((promo) => {
    const { code, description, legalText, isCouponApplicable } = promo;
    const {
      type = '',
      startDate = '',
      endDate = '',
      status = '',
      stamp = '',
    } = getCouponDetails(codeTypeMap, code);
    const endDatepst = convertExpiryDateToPST(endDate, couponExpiryTimeDiff);

    if (!isValidEndDate(endDatepst)) {
      logger.error('simulateToOffer error', description, endDate);
    }

    return {
      isApplied: status === 'applied',
      isOnHold:
        status === COUPON_STATUS.NO_APPLICABLE_PROMOTION && isCouponOnHoldNewDesignEnabled
          ? 'true'
          : false,
      validFrom: startDate,
      validTo: endDatepst,
      offerType: type,
      offerCode: code,
      offerText: description || couponDescriptonFromPriceAdjustments[stamp],
      offerDescription: description || couponDescriptonFromPriceAdjustments[stamp],
      legalText,
      isExpired: endDatepst
        ? Math.round((endDatepst.getTime() - now.getTime()) / oneDay) < 0
        : false,
      isCouponApplicable: isCouponApplicable === undefined || isCouponApplicable,
    };
  });
};
const getCouponList = (promoListWithType) => {
  const couponList = [];
  promoListWithType.forEach((cpn) => {
    return couponList.push(cpn.code || cpn.couponCode);
  });
  return couponList;
};

export const getPromotionDetails = (
  promoListWithType = [],
  errorsMapping,
  isCouponOnHoldNewDesignEnabled,
  couponExpiryTimeDiff,
  couponDescriptonFromPriceAdjustments
) => {
  return executeStatefulGraphQLQuery({
    query: getPromotionDetailsQuery,
    variables: {
      couponCode: getCouponList(promoListWithType),
    },
    type: 'query',
  }).then((res) => {
    let simulatedPromoList = [];
    const respData = res?.data?.getPromotionDetails?.couponDetails;
    if (respData) {
      simulatedPromoList = simulateToOffer(
        respData,
        promoListWithType,
        isCouponOnHoldNewDesignEnabled,
        couponExpiryTimeDiff,
        couponDescriptonFromPriceAdjustments
      );
    }
    return constructCouponStructure(simulatedPromoList, errorsMapping);
  });
};
export default {
  applyCouponToBasket,
  removeCouponOrPromo,
  getPromotionDetails,
};
